import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ClipboardCheck, LogIn } from 'lucide-react';
import api, { trainerHeuteAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useSitzungVerlaengern, useWachBleiben } from '@/hooks/useStation';
import { Button } from '@/components/ui/button';
import AnwesenheitTafel from '@/components/AnwesenheitTafel';

/* Trainer-Anwesenheit im CRM (22.09.2026, Omoplata-Parität 1.6) — Tablet am
   Matteneingang. Seit 28.09.2026 teilt sich die Seite die Tafel mit dem
   externen Trainer-Link (/trainer/<token>); hier laeuft alles ueber den
   CRM-Login, dazu Schnellerfassung und Sprung zur Lead-Karte.

   Kiosk-Grundlage (03.10.2026): laeuft die Seite mit der Stations-Rolle „Trainer-Tablet",
   laedt sie alle 30 Sekunden von selbst neu, haelt den Bildschirm wach und verlaengert ihre
   30-Tage-Sitzung. Ist die Sitzung trotzdem abgelaufen, steht hier ein klarer Hinweis statt
   einer leeren Seite. */
export default function TrainerHeutePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const station = user?.station === 'trainer';
  const [abgemeldet, setAbgemeldet] = useState(false);
  useWachBleiben(station);
  useSitzungVerlaengern(station);

  const tafelApi = useMemo(() => ({
    heute: () => trainerHeuteAPI.heute(),
    status: (bid, s) => trainerHeuteAPI.status(bid, s),
    hinzufuegen: (cid, mid) => trainerHeuteAPI.hinzufuegen(cid, mid),
    suche: async (q) => (await api.get('/leads', { params: { search: q, limit: 8 } })).data.leads || [],
    sprache: (cid, x) => trainerHeuteAPI.sprache(cid, x),
    anwesendMehrere: (cid, ids) => trainerHeuteAPI.anwesendMehrere(cid, ids),
    notiz: (mid, text) => trainerHeuteAPI.notiz(mid, text),
    schnellerfassung: (cid, body) => trainerHeuteAPI.schnellerfassung(cid, body),
    mitgliedOeffnen: (mid) => navigate(`/leads/${mid}`),
  }), [navigate]);
  const ladeFehler = useCallback((e) => { if (e?.response?.status === 401) setAbgemeldet(true); }, []);

  return (
    <div className="space-y-4 fade-in" data-testid="trainer-heute-page">
      <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2"><ClipboardCheck className="w-6 h-6 text-primary" /> Anwesenheit heute</h1>
      {abgemeldet ? (
        <div className="rounded-xl border border-amber-300 bg-amber-50 dark:bg-amber-900/20 p-6 text-center space-y-3" data-testid="trainer-abgemeldet">
          <p className="text-lg font-semibold">Bitte neu anmelden</p>
          <p className="text-sm text-muted-foreground">Die Anmeldung dieses Geräts ist abgelaufen. Nach der Anmeldung geht es hier weiter.</p>
          <Button onClick={() => { window.location.href = '/login'; }} className="gap-1.5"><LogIn className="w-4 h-4" /> Zur Anmeldung</Button>
        </div>
      ) : (
        <AnwesenheitTafel api={tafelApi} intervallMs={station ? 30000 : 60000} onLadeFehler={ladeFehler} />
      )}
    </div>
  );
}
